import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {DjPsychoGameAssets} from "../DjPsychoGameAssets";
import {TimelineLite} from "gsap";
import {DjPsycho} from "../DjPsycho";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ParticleController} from "../components/particles/ParticleController";
import {ParticleSettings} from "../components/particles/ParticleSettings";

export class DjPsychoBigWinView extends PIXI.Container implements StageLayer {
    private _countUp: BasicBitmapCountUp;
    private _bigWin: PIXI.spine.Spine;
    private _bigWinSprite: TimelineSprite;
    private _wave: TimelineSprite;
    private _particleEffectsLoops: ParticleController[] = [];
    private _particleEffectsSplash: ParticleController[] = [];
    private _gradientOverlay: PIXI.Graphics;

    constructor() {
        super();
        this.name = "DjPsychoBigWinView";
        this.init();
    }

    public hide(duration: number = 0.0001): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.to(this, {duration: duration, alpha: 0});
        tl.add(() => {
            this._bigWin.visible = false;
            this._bigWin.state.clearTracks();
            this._bigWinSprite.hide();
            this._bigWinSprite.stopLoop();
            this._wave.hide();
            this._wave.stopLoop();
            this._gradientOverlay.visible = false;
            this.emitFountain(-1);
            this.emitSplash(-1);
        });
        return tl;
    }

    public getLevelAnimation(countFrom: number, countTo: number, duration: number, level: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            scaleTl: TimelineLite = new TimelineLite();

        scaleTl.add(this.getCountUpScaleAnimation(0.1, "power2.in", 0.8));
        scaleTl.add(this.getCountUpScaleAnimation(0.2, "power2.out", 1.0));
        scaleTl.add(this.getCountUpScaleAnimation(duration - 0.3, "power2.in", 1.2));

        tl.add([
            () => {
                if (level === 1) {
                    this.alpha = 1;
                    this._countUp.visible = true;
                    this._bigWin.visible = true;
                    this._bigWin.updateTransform();
                }
                this._bigWin.skeleton.setToSetupPose();
                this._bigWin.skeleton.setSlotsToSetupPose();
                this._bigWin.state.clearTracks();
                this._bigWin.state.setEmptyAnimation(0, 0);
                this._bigWin.state.setAnimation(0, `BW_${level}_In`, false);
                this.emitWave(level);
                this.emitFountain(level);
                this.emitSplash(level);

                this._gradientOverlay.clear();
                this._gradientOverlay.beginTextureFill(DjPsycho.gradient(DjPsycho.gradientColorLevel[level - 1])).drawRect(0, 0, NolimitApplication.screenBounds.width * 2.5, NolimitApplication.screenBounds.height * 2.5).endFill();
                this._gradientOverlay.visible = true;
                if (level > 2) {
                    this._bigWinSprite.show();
                    this._bigWinSprite.playLoop();
                }
            },
            scaleTl,
            this._countUp.getCountAnimation(countFrom, countTo, duration),
        ], 0);
        tl.add(() => {
            this._bigWin.state.setAnimation(0, `BW_${level}_Loop`, true);
        }, 30 / 30);
        tl.add(() => {
            this._bigWin.state.setAnimation(0, `BW_${level}_Out`, false);
        });
        return tl;
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._countUp.position.set(360, 580);
            this._bigWinSprite.position.set(360, 450);
            this._wave.position.set(360, 200);
        } else {
            this._countUp.position.set(360, 740);
            this._bigWinSprite.position.set(360, 720);
            this._wave.position.set(360, -100);
        }
    }

    private initFountains(container: PIXI.Container): void {
        this._particleEffectsLoops.push(DjPsycho.particleManager.createAnimation(container, "AlienLoop", "P_Alien", ParticleSettings.coinsConfigs, true));
        this._particleEffectsSplash.push(DjPsycho.particleManager.createAnimation(container, "AlienSplash", "P_Alien", ParticleSettings.coinsConfigsSplash, true));

        this._particleEffectsLoops.push(DjPsycho.particleManager.createAnimation(container, "XtcLoop", "Xtc", ParticleSettings.coinsConfigs, true));
        this._particleEffectsSplash.push(DjPsycho.particleManager.createAnimation(container, "XtcSplash", "Xtc", ParticleSettings.coinsConfigsSplash, true));
    }

    private init(): void {
        const container: PIXI.Container = new PIXI.Container();

        this._gradientOverlay = new PIXI.Graphics().beginTextureFill(DjPsycho.gradient(DjPsycho.gradientColorLevel[0])).drawRect(-480, -480, 1680, 1680).endFill();
        this._gradientOverlay.position.set(-500, -480);
        this._gradientOverlay.visible = false;
        this._gradientOverlay.alpha = 0.7;

        this._countUp = new BasicBitmapCountUp({
            fontName: DjPsychoGameAssets.BITMAP_FONT_WIN_COUNT_UP_BW,
            fontSize: 52,
        }, 580, new PIXI.Point(0.5, 0.5));
        this._countUp.visible = false;

        this._bigWin = new PIXI.spine.Spine(DjPsycho.assetLoader.getSpineAsset("bigWinInSpine"));
        this._bigWin.position.set(360);
        this._bigWin.visible = false;

        this._bigWinSprite = new TimelineSprite(DjPsycho.assetLoader.getTextures("FX_EQ/FX_EQ_"));
        this._bigWinSprite.anchor.set(0.5);
        this._bigWinSprite.blendMode = PIXI.BLEND_MODES.ADD;
        this._bigWinSprite.visible = false;
        this._bigWinSprite.scale.set(2);

        this._wave = new TimelineSprite(DjPsycho.assetLoader.getTextures("FX_Wave/FX_Wave_"), 15);
        this._wave.anchor.set(0.5);
        this._wave.blendMode = PIXI.BLEND_MODES.ADD;
        this._wave.visible = false;
        this._wave.scale.set(3);

        this.onResize();
        container.addChild(this._gradientOverlay, this._wave, this._bigWinSprite, this._bigWin);
        this.initFountains(container);
        this.addChild(container, this._countUp);
        this.hide();
    }


    private emitFountain(level: number): void {
        this._particleEffectsLoops.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });
        if (level > 4) {
            this._particleEffectsLoops[0].enableEmit(true);
            this._particleEffectsLoops[1].enableEmit(true);
        } else if (level >= 4) {
            this._particleEffectsLoops[0].enableEmit(true);
        }
    }

    private emitWave(level: number): void {
        if (level > 1) {
            this._wave.show();
            this._wave.playLoop();
        }
    }

    private emitSplash(level: number): void {
        this._particleEffectsSplash.forEach((p: ParticleController) => {
            p.enableEmit(false);
        });
        if (level > 4) {
            this._particleEffectsSplash[0].enableEmit(true);
            this._particleEffectsSplash[1].enableEmit(true);
        } else if (level >= 4) {
            this._particleEffectsSplash[0].enableEmit(true);
        }
    }

    private getCountUpScaleAnimation(duration: number, ease: string, to: number, from?: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (from != undefined) {
            tl.add(() => {
                this._countUp.scale.set(from, from)
            });
        }
        tl.to(this._countUp.scale, {duration: duration, x: to, y: to, ease: ease});
        return tl;
    }
}